import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";

const variantClass = {
  gold: "btn-gold",
  ghost: "btn-ghost",
  crimson: "btn-crimson",
};

/**
 * A button/link with subtle magnetic cursor attraction.
 * Pass `to` to render a router Link, or `onClick` for a button.
 */
export default function MagneticButton({
  children,
  to,
  href,
  onClick,
  variant = "gold",
  className = "",
  strength = 0.35,
  type = "button",
  disabled = false,
  ...rest
}) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 18, mass: 0.4 });

  const handleMove = (e) => {
    if (disabled) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    x.set(relX * strength);
    y.set(relY * strength);
  };

  const reset = () => {
    x.set(0);
    y.set(0);
  };

  const cls = `${variantClass[variant] || "btn-gold"} inline-block ${className}`;
  const motionProps = {
    ref,
    onMouseMove: handleMove,
    onMouseLeave: reset,
    style: { x: sx, y: sy },
    className: cls,
  };

  if (to) {
    return (
      <motion.div {...motionProps} style={{ ...motionProps.style, display: "inline-block" }}>
        <Link to={to} onClick={onClick} {...rest} style={{ display: "inline-block", width: "100%", height: "100%" }}>
          {children}
        </Link>
      </motion.div>
    );
  }

  if (href) {
    return (
      <motion.a href={href} onClick={onClick} {...motionProps} {...rest}>
        {children}
      </motion.a>
    );
  }

  return (
    <motion.button type={type} onClick={onClick} disabled={disabled} {...motionProps} {...rest}>
      {children}
    </motion.button>
  );
}
